import React, { useState, useMemo, useContext, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import {
  Search,
  Download,
  RotateCcw,
  Eye,
  Calendar,
  Filter,
  X,
  AlertTriangle,
  CheckCircle,
  FileText,
  Home,
  Copy,
  TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';
import { chartExtractionRuns, chartExtractionKPIs, ChartExtractionRun, ChartExtractionChart } from '../src/Utils/chartExtractionMockData';
import { AppContext } from '../src/App';
import { apiClient } from '@/Utils/apiClient';
import { useAuth } from './AuthContext';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { PaginationCustom } from './ui/pagination-custom';
import kantime from '../src/assets/kanTime.png';
import { toast } from 'sonner';
import { errorResponse, formatDate } from '@/Utils/helpers';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/config';
// import demoLabels from '@/Utils/labels/demoLabels';

const CHART_TYPES = ['CSC', 'DLS', 'SN', 'AIDE'] as const;
const RUN_TYPES = ['Test', 'Real'] as const;
const RUN_STATUSES = ['Passed', 'Failed', 'Partial'] as const;
interface batchChart {
  updated_at: string,
  created_at: string,
  detailed_log: string[],
  errors: string,
  run_status: string,
  failed_charts: number,
  successful_charts: number,
  total_charts: number,
  average_time_ms: number,
  total_time_ms: number,
  end_datetime: string,
  start_datetime: string,
  chart_type: string,
  run_type: string,
  batch_name: string,
  run_id: number
  total_retries: number
}
interface batchData {
  summary: {
    retryRatePercentage: number;
    averageTimePerChartSeconds: number
    failedCharts: number
    successfulCharts: number
    totalChartsProcessed: number
    partialBatches: number
    failedBatches: number
    passedBatches: number
    totalBatches: number
  }
  data: batchChart[],
  pagination: {
    totalPages: number,
    total: number,
    limit: number,
    page: number
  }
}

export function ChartExtractionRuns() {
  const { setCurrentView, setSelectedExtractionRunData, orgConfig } = useContext(AppContext);
  const { token } = useAuth();
  const navigate = useNavigate();
  // Filter states
  const [dateRange, setDateRange] = useState('7d');
  const [selectedChartTypes, setSelectedChartTypes] = useState<Set<string>>(new Set());
  const [runType, setRunType] = useState('all');
  const [runStatus, setRunStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFailuresOnly, setShowFailuresOnly] = useState(false);
  const [batchFiles, setBatchFiles] = useState<batchData>([]);

  // UI states
  const [selectedChart, setSelectedChart] = useState<ChartExtractionChart | null>(null);
  const [showChartLogs, setShowChartLogs] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  //pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;



  const fetchData = async () => {
    let startISO = dateRange === 'all' ? null : '', endISO = dateRange === 'all' ? null : '';

    if (dateRange && dateRange !== "all") {
      const days = parseInt(dateRange.replace("d", ""), 10);
      const now = new Date();

      let start, end;

      if (days === 1) {
        // Last 24 hours → rolling
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        end = now;
      } else {
        // Last 7 or 30 days → full days (midnight range)
        start = new Date();
        start.setDate(now.getDate() - days);
        start.setHours(0, 0, 0, 0);

        end = new Date();
        end.setHours(23, 59, 59, 999);
      }


      const startDate = formatDate(start);
      const endDate = formatDate(end);
      startISO = startDate;
      endISO = endDate;
    }

    const filterItems = {
      startDate: startISO,
      endDate: endISO,
      chartType: selectedChartTypes.size > 0 ? Array.from(selectedChartTypes).join(',') || 'all' : null,
      runType: (runType && runType !== 'all') ? runType : null,
      runStatus: showFailuresOnly ? 'Failed' : (runStatus && runStatus !== 'all') ? runStatus : null,
      searchTerm: searchTerm || null,
      page: currentPage,
      limit: itemsPerPage

    }
    const params = new URLSearchParams();
    for (const key in filterItems) {
      if (filterItems[key] != null) { // Only append if the value exists
        params.append(key, filterItems[key]);
      }
    }

    try {
      const response = await apiClient(`extract/chart/logs?${params.toString()}`,
        {
          method: 'GET',
          token
        }
      )

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error)
      }
      setBatchFiles(result);
    }
    catch (error) {
      console.log(error)
      const errMsg = errorResponse(error);
      toast.error(`Error fetching data: ${errMsg}`)
    }



  }
  useEffect(() => {
    fetchData();
  }, [currentPage, selectedChartTypes, runType, runStatus, searchTerm, showFailuresOnly, dateRange])

  // useEffect(() => {
  //     fetchData()
  //   }, [currentPage])
  // Filter chart types
  const toggleChartType = (chartType: string) => {
    const newSelected = new Set(selectedChartTypes);
    if (newSelected.has(chartType)) {
      newSelected.delete(chartType);
    } else {
      newSelected.add(chartType);
    }
    setSelectedChartTypes(newSelected);
    setCurrentPage(1);
  };

  const clearChartTypeFilters = () => {
    setSelectedChartTypes(new Set());
    setCurrentPage(1);
  };

  // Filtered runs
  //   const filteredRuns = useMemo(() => {
  //     let runs = batchFiles?.data || [];

  //     // Date range filter
  //     if (dateRange !== 'all') {
  //       const days = parseInt(dateRange.replace('d', ''));
  //       const cutoff = new Date();
  //       cutoff.setDate(cutoff.getDate() - days);
  //        let ISOFormate=new Date(cutoff)
  //         let cutoffDate=ISOFormate.toISOString()

  //       runs = runs.filter(run => {
  //         console.log(run.start_datetime,"run.start_datetime")

  //         return run.start_datetime>= cutoffDate});

  //     }

  //     // Chart type filter
  //     if (selectedChartTypes.size > 0) {
  //         // const chartType=selectedChartTypes.split('-')
  //       runs = runs.filter(run => selectedChartTypes.has(run.chart_type));

  //     }

  //     // Run type filter
  //     if (runType !== 'all') {
  //       runs = runs.filter(run => run.run_type.toLowerCase() === runType);

  //     }

  //     // Run status filter
  //     if (runStatus !== 'all') {
  //       runs = runs.filter(run => run.run_status.toLowerCase() === runStatus);

  //     }

  //     // Search filter
  //     if (searchTerm) {
  //       const term = searchTerm.toLowerCase();
  //       runs = runs.filter(run =>
  //         run.batch_name.toLowerCase().includes(term) ||
  //         run.run_id.toLowerCase().includes(term)
  //         // run.charts.some(chart => chart.chartId.toLowerCase().includes(term))
  //       );

  //     }

  //     // Show failures only
  //     if (showFailuresOnly) {
  //       runs = runs.filter(run => run.run_status === 'Failed' || run.failed_charts > 0);

  //     }

  //     return runs.sort(
  //   (a, b) => new Date(b.start_datetime).getTime() - new Date(a.start_datetime).getTime()
  // );

  //   }, [dateRange, selectedChartTypes, runType, runStatus, searchTerm, showFailuresOnly,batchFiles]);

  // KPI calculations based on filtered data
  //   const filteredKPIs = useMemo(() => {
  //     const totalRuns = filteredRuns.length;
  //     const passedRuns = filteredRuns.filter(r => r.run_status=== 'Passed').length;
  //     const failedRuns = filteredRuns.filter(r => r.run_status=== 'Failed').length;
  //     const totalCharts = filteredRuns.reduce((sum, r) => sum + r.total_charts
  // , 0);
  //     const succeededCharts = filteredRuns.reduce((sum, r) => sum + r.successful_charts
  // , 0);
  //     const failedCharts = filteredRuns.reduce((sum, r) => sum + r.failed_charts, 0);
  //     const avgTimePerChart = totalCharts > 0 ? 
  //       filteredRuns.reduce((sum, r) => sum + r.average_time_ms, 0) / filteredRuns.length : 0;
  //     const retryRate = totalCharts > 0 ? 
  //       (filteredRuns.reduce((sum, r) => sum + r.retries||0, 0) / totalCharts) * 100 : 0;

  //     return {
  //       totalRuns,
  //       passedRuns,
  //       failedRuns,
  //       totalCharts,
  //       succeededCharts,
  //       failedCharts,
  //       avgTimePerChart,
  //       retryRate
  //     };
  //   }, [filteredRuns]);
  // pagination
  const totalItems = batchFiles?.pagination?.total || 0;
  const totalPages = batchFiles?.pagination?.totalPages || Math.ceil(batchFiles?.data?.length / itemsPerPage);
  const getRunTypeColor = (type: string) => {
    return type === 'Test' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800';
  };
  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };
  const getChartTypeColor = (type: string) => {
    const colors = {
      'CSC - Section 28': 'bg-green-100 text-green-800',
      'DLS - Section 17': 'bg-blue-100 text-blue-800',
      'SN': 'bg-orange-100 text-orange-800',
      'Aide': 'bg-purple-100 text-purple-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Passed': return 'bg-green-100 text-green-800';
      case 'Failed': return 'bg-red-100 text-red-800';
      case 'In-Progress': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const handleRunClick = (run: batchChart) => {
    // Preserve existing context usage so the detail screen can reuse this data
    setSelectedExtractionRunData(run);
    setCurrentView('chart-extraction-run-detail');
    // Navigate to the new route with the run id in the URL
    navigate(ROUTES.chartExtractionDetail, {
      state: { runData: run }
    });
  };

  const handleChartLogClick = (chart: ChartExtractionChart) => {
    setSelectedChart(chart);
    setShowChartLogs(true);
  };

  const handleRerunFailed = (runId: string) => {
    setNotification({
      type: 'success',
      message: `Rerun initiated for failed charts in ${runId}`
    });
    setTimeout(() => setNotification(null), 5000);
  };

  const exportToCsv = async (run: batchChart) => {
    // const csvContent = [
    //   ['Chart Number', 'Chart ID', 'Goals', 'Planned Chunks', 'Attempts', 'Processing Time (ms)', 'Aggregation Time (ms)', 'Status', 'Failure Reason'],
    //   ...run.charts.map(chart => [
    //     chart.chartNumber,
    //     chart.chartId,
    //     chart.goals,
    //     chart.plannedChunks,
    //     chart.attempts,
    //     chart.processingTime,
    //     chart.aggregationTime,
    //     chart.status,
    //     chart.failureReason || ''
    //   ])
    // ].map(row => row.join(',')).join('\n');
    try {
      const response = await apiClient(`extract/chart/logs/download/${run.run_id}`, {
        method: 'GET',
        token
      })
      const result = await response.json();



      // const blob = new Blob([csvContent], { type: 'text/csv' });
      // const url = URL.createObjectURL(blob);
      // const a = document.createElement('a');
      // a.href = url;
      // a.download = `chart_extraction_${run.runId}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      // a.click();
      // URL.revokeObjectURL(url);
      const zipBlob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });

      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chart_extraction_${run.run_id}_${format(new Date(), 'yyyy-MM-dd')}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
    catch (error) {
      console.error('Download error:', error);
      alert(`Download failed: ${error.message}`);
    }

  };
  //   console.log(dateRange,"tt")
  //   const days = parseInt(dateRange.replace('d', ''));
  //       const cutoff = new Date();
  //       cutoff.setDate(cutoff.getDate() - days);
  //       let fdate=new Date(cutoff)
  //         let cutoffDate=fdate.toISOString()
  // console.log(batchFiles?.data,filteredRuns,chartExtractionRuns,"qwertyuiop")
  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      // showCopyFeedback(buttonId);
      toast.success('Text copied to clipboard', {
        description: `${content} copied successfully`
      });
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy to clipboard');
    }
  }
  return (
    <div>
      {/* Notification Banner */}
      {notification && (
        <div className={`fixed top-0 left-0 right-0 z-50 p-4 text-center text-white ${notification.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}>
          <div className="flex items-center justify-center gap-2">
            {notification.type === 'success' ?
              <CheckCircle className="w-5 h-5" /> :
              <AlertTriangle className="w-5 h-5" />
            }
            <span>{notification.message}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setNotification(null)}
              className="text-white hover:bg-white/20"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}



      <div className="p-6 space-y-6">
        <div className=" mx-auto">
          <div className="mb-1">
            <Breadcrumb>
              <BreadcrumbList>
                {/* <BreadcrumbItem>
                      <BreadcrumbLink 
                        href="#" 
                        className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
                        onClick={() => setCurrentView('dashboard')}
                      >
                        <Home className="w-3 h-3" />
                        Dashboard
                      </BreadcrumbLink>
                    </BreadcrumbItem> */}
                {/* <BreadcrumbSeparator /> */}
                <BreadcrumbItem>
                  <BreadcrumbLink
                    href="#"
                    className="text-gray-600 hover:text-gray-900"
                    onClick={() => setCurrentView('chart-extraction-runs')}
                  >
                    <div className='flex items-center gap-2'> <TrendingUp className="w-4 h-4" />  Chart Extraction Runs</div>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {/* <BreadcrumbSeparator /> */}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className='flex items-center gap-4'>
              <div><h1 className="text-2xl font-bold text-gray-900">Chart Extraction Runs</h1></div>
              {orgConfig?.logo &&
                <div>
                  <img src={orgConfig?.logo} alt={`${orgConfig?.name} Logo`} className="h-10 w-26" />
                </div>
              }
            </div>

            <p className="text-gray-600 mt-1 text-base">Monitor automated healthcare chart extraction processes</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="rounded-2xl shadow-sm">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-2">Runs</div>
                <div className="text-2xl font-bold text-gray-900 mb-2">{batchFiles?.summary?.totalBatches}</div>
                <div className="flex justify-center gap-4 text-sm">
                  <span className="text-green-600">{batchFiles?.summary?.passedBatches} Passed</span>
                  <span className="text-red-600">{batchFiles?.summary?.failedBatches} Failed</span>
                  <span className="text-gray-600">{batchFiles?.summary?.partialBatches} Partial</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-2">Charts Processed</div>
                <div className="text-2xl font-bold text-gray-900 mb-2">{batchFiles?.summary?.totalChartsProcessed}</div>
                <div className="flex justify-center gap-4 text-sm">
                  <span className="text-green-600">{batchFiles?.summary?.successfulCharts} Succeeded</span>
                  <span className="text-red-600">{batchFiles?.summary?.failedCharts} Failed</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-2">Avg Time per Chart</div>
                <div className="text-2xl font-bold text-gray-900">{formatTime(batchFiles?.summary?.averageTimePerChartSeconds || 0)}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl shadow-sm">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-2">Retry Rate</div>
                <div className="text-2xl font-bold text-gray-900">{batchFiles?.summary?.retryRatePercentage.toFixed(1)}%</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Bar */}
        <Card className="rounded-2xl shadow-sm sticky top-0 z-10">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Date Range */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <Select value={dateRange} onValueChange={(value) => { setDateRange(value); setCurrentPage(1); }}>
                  <SelectTrigger className="w-36 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1d">Last 24 hours</SelectItem>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="all">All time</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Chart Types */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Chart Types:</span>
                <div className="flex gap-2">
                  {CHART_TYPES.map(type => (
                    <Button
                      key={type}
                      variant={selectedChartTypes.has(type) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleChartType(type)}
                      className="text-sm"
                    >
                      {type.split(' ')[0]}
                    </Button>
                  ))}
                  {selectedChartTypes.size > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearChartTypeFilters}
                      className="text-sm"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Run Type */}
              <Select value={runType} onValueChange={(value) => { setRunType(value); setCurrentPage(1); }}>
                <SelectTrigger className="w-32 text-sm">
                  <SelectValue placeholder="Run Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Test">Test</SelectItem>
                  <SelectItem value="Real">Real</SelectItem>
                </SelectContent>
              </Select>

              {/* Run Status */}
              <Select value={runStatus} onValueChange={(value) => {
                setRunStatus(value);     // update runStatus
                setCurrentPage(1);       // reset to first page
              }}>
                <SelectTrigger className="w-36 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Passed">Passed</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                  <SelectItem value="Partial">Partial</SelectItem>
                  <SelectItem value="InProgress">In Progress</SelectItem>

                </SelectContent>
              </Select>

              {/* Search */}
              <div className="flex-1 min-w-64">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 text-sm" />
                  <Input
                    placeholder="Search batch name or run ID"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value), setCurrentPage(1) }}
                    className="pl-10 text-sm"
                  />
                </div>
              </div>

              {/* Show Failures Only */}
              {/* <Button
                variant={showFailuresOnly ? "default" : "outline"}
                size="sm"
                onClick={() => {setShowFailuresOnly(!showFailuresOnly), setCurrentPage(1);}}
                className="flex items-center gap-2 text-sm"
              >
                <AlertTriangle className="w-4 h-4" />
                Failures Only
              </Button> */}
            </div>
          </CardContent>
        </Card>

        {/* Runs Table */}
        <Card className="rounded-2xl shadow-sm">
          <CardHeader>
            <CardTitle>Extraction Runs ({batchFiles?.pagination?.total})</CardTitle>
          </CardHeader>
          <CardContent>
            {batchFiles?.data?.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No runs found for the selected filters.</p>
              </div>
            ) : (
              <div className="rounded-md border p-2">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Start Time</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Batch Name</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Run ID</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">Run Type</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">Chart Type</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center"># of Charts</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Succeeded</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Failed</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">Total Runtime</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195] text-center">Avg per Chart</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Retries</TableHead>
                      <TableHead className="text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Status</TableHead>
                      <TableHead className="w-32  text-[14px] font-inter font-semibold leading-[100%] tracking-[-0.5%] whitespace-normal break-words uppercase text-[#758195]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batchFiles?.data?.map((run) => (
                      <TableRow
                        key={run.id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleRunClick(run)}
                      >
                        <TableCell>
                          <div className="text-sm text-gray-800">
                            {/* {format(new Date(run.
start_datetime), 'MMM dd, yyyy')} */}

                            {run?.start_datetime ? new Date(run?.start_datetime)?.toLocaleDateString('en-US') : ''}
                          </div>
                          <div className="text-xs text-gray-500">
                            {/* {format(new Date(run.
start_datetime), 'HH:mm:ss')} */}
                            {run?.start_datetime ? new Date(run?.start_datetime)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', }) : ''}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-gray-800"><span className="flex items-center gap-1">{run.batch_name}<span>{run.batch_name && <Copy className="w-4 h-4 ml-1" onClick={(e) => { e.stopPropagation(), copyToClipboard(run.batch_name) }} />}</span></span></TableCell>
                        <TableCell className="font-mono text-sm text-gray-800"><span className="flex items-center gap-1 font-inter">{run.run_id}<span>{run.batch_name && <Copy className="w-4 h-4 ml-1" onClick={(e) => { e.stopPropagation(), copyToClipboard(run.run_id) }} />}</span></span></TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${getRunTypeColor(run.run_type)}`}>
                            {run.run_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${getChartTypeColor(run.chart_type)}`}>
                            {run.chart_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-gray-800">{run.total_charts}</TableCell>
                        <TableCell className="text-center text-green-600">{run.successful_charts}</TableCell>
                        <TableCell className="text-center text-red-600">{run.failed_charts}</TableCell>
                        <TableCell className="text-center text-gray-800">{formatTime(run.total_time_ms || 0)}</TableCell>
                        <TableCell className="text-center text-gray-800">{formatTime(run.average_time_ms || 0)}</TableCell>
                        <TableCell className="text-center text-gray-800">{run.total_retries || 0}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${getStatusColor(run.run_status
                          )}`}>
                            {run.run_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRunClick(run);
                              }}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                exportToCsv(run);
                              }}
                              title="Download JSON"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {run.failed > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRerunFailed(run.runId);
                                }}
                                title="Re-run Failed Charts"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t bg-gray-50">
                    <div className="flex justify-between items-center">
                      {/* <span className="text-sm text-gray-600">
                        Showing {totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
                      </span> */}

                      <PaginationCustom
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        onItemsPerPageChange={(newItemsPerPage) => {
                          onPageChange(1); // Reset page to 1
                        }}
                      // showPageSizeSelector={false}  // ❌ Hides "items per page"
                      // showItemCount={false}         // ✅ Because we manually show the count above
                      />
                    </div>
                  </div>



                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>



      {/* Chart Log Viewer */}
      <Dialog open={showChartLogs} onOpenChange={setShowChartLogs}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Chart Log: {selectedChart?.chartId}</DialogTitle>
            <DialogDescription>
              Processing logs for chart {selectedChart?.chartNumber}
            </DialogDescription>
          </DialogHeader>

          {selectedChart && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Status:</span>
                  <Badge className={`ml-2 text-xs ${getStatusColor(selectedChart.status)}`}>
                    {selectedChart.status}
                  </Badge>
                </div>
                <div>
                  <span className="text-gray-600">Processing Time:</span>
                  <span className="ml-2 font-medium">{formatTime(selectedChart.processingTime)}</span>
                </div>
                <div>
                  <span className="text-gray-600">Attempts:</span>
                  <span className="ml-2 font-medium">{selectedChart.attempts}</span>
                </div>
              </div>

              <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm max-h-96 overflow-y-auto">
                {selectedChart.logLines.map((log, index) => (
                  <div key={index} className="mb-1">{log}</div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


    </div>
  );
}